/**
 * @fileoverview Device utility functions. Note that this file uses `curry` for some functions,
 * allowing them to be called either fully or partially. We use this sparingly, as there is a
 * performance cost, particularly on Hermes.
 */

import { curry } from '@eturi/util'
import capitalize from 'lodash/capitalize.js'
import isEmpty from 'lodash/isEmpty.js'
import type {
	Device,
	DeviceAndroid,
	DeviceIOS,
	DeviceIOSSupervised,
	DeviceOS,
	DeviceTypeAccount,
	DeviceTypeManaged,
	DeviceWithIOSChildApp,
	ManagedDevice,
	RawDevice,
	RawManagedDevice,
} from '../types/Device.js'
import { ChildAppBundle, DeviceType } from '../types/Device.js'
import type { FeatureRequirement, FeatureRequirementResult } from '../types/FeatureInfo.js'
import { FeatureRequirements } from '../types/FeatureInfo.js'
import type { PairRequiredStatus } from '../types/PairStatus.js'
import { PairStatuses, type PairRequired, type PairStatus } from '../types/PairStatus.js'

export const WEB_PARENT_APP_NAME = 'WebApp-Parent'

export const getPairStatus = (d: RawDevice): PairStatus => {
	if (d.management_removed || d.retired || isEmpty(d.users)) return 'not_paired'
	if (!isIOSDevice(d)) return 'full_pair'
	return d.management_level == 'ios-supervised' ? 'full_pair' : 'quick_pair'
}

export const getPairRequiredStatus = (
	devices: ManagedDevice[],
	userId: string,
	pairRequired: PairRequired,
): PairRequiredStatus => {
	const userDevices = devices.filter((device) => device.users?.includes(userId))

	if (!userDevices.length) return 'no_devices'

	if (pairRequired == 'quick_pair') return 'paired'

	const allDevicesFullPaired = userDevices.every((d) => getPairStatus(d) == 'full_pair')

	return (
		allDevicesFullPaired ? 'paired'
		: userDevices.length > 1 ? 'some_devices_paired'
		: 'full_pair_required'
	)
}

export const isAccountDevice = <T extends RawDevice>(d: T): d is DeviceTypeAccount<T> =>
	d.type === DeviceType.Account

export const isManagedDevice = <T extends RawDevice>(d: T): d is DeviceTypeManaged<T> =>
	d.type === DeviceType.Managed

const ANDROID_LOWER = 'android'
const IOS_LOWER = 'ios'

export const isAndroidDevice = <T extends Pick<RawDevice, 'os'>>(d: T): d is DeviceAndroid<T> =>
	d.os?.toLowerCase() === ANDROID_LOWER

// TODO: Do we need "Apple" distinction? We care about iOS.
// export const isAppleDevice = <T extends Pick<RawDevice, 'make'>>(
// 	d: T,
// ): d is T & WithDeviceOS<'iOS'> => (d.make || '').toLowerCase() === 'apple'

export const isIOSDevice = <T extends Pick<RawDevice, 'os'>>(d: T): d is DeviceIOS<T> =>
	d.os?.toLowerCase() === IOS_LOWER

export const isIOSSupervisedDevice = <T extends RawManagedDevice>(
	d: T,
): d is DeviceIOSSupervised<T> => d.management_level == 'ios-supervised'

export const hasIOSChildApp = <T extends RawManagedDevice>(d: T): d is DeviceWithIOSChildApp<T> =>
	d.system_components === ChildAppBundle.IOS

export const hasMinOSVer = (minOSVer: number, d: ManagedDevice) =>
	Number.parseFloat(toMajorMinorVersion(d.os_version)) >= minOSVer

export const hasMinFeatVer = (minFeatVer: number, d: ManagedDevice) =>
	Number.parseFloat(d.app_version) >= minFeatVer

export const toMajorMinorVersion = (v: Maybe<string>): string => {
	if (!v) return ''

	const splitVersion = v.split('.')
	// Handles just major version i.e. 1
	if (splitVersion.length === 1) return v

	return splitVersion[0] + '.' + splitVersion[1]
}

export const normalizeDeviceOSStr = <T extends Pick<RawDevice, 'os'>>(d: T): DeviceOS =>
	isAndroidDevice(d) ? 'Android'
	: isIOSDevice(d) ? 'iOS'
	: 'Unknown'

export const getNormalizedModelName = <T extends RawDevice>(d: T): string =>
	d.model_name || d.display_name || d.model || 'Unknown'

// FIXME: Normalize display name. Prioritize model_name -> model -> display_name
export const getDeviceDisplayName = (d: RawDevice): string => {
	if (d.display_name) return d.display_name

	const make = capitalize(d.make || 'Unknown')
	const model = getNormalizedModelName(d)

	return isIOSDevice(d) ? `${make} ${model}` : model
}

export const mapRawToDevice = <T extends RawDevice>(raw: T): T & Device => {
	const make = capitalize(raw.make || 'Unknown')
	const model = getNormalizedModelName(raw)
	const os = normalizeDeviceOSStr(raw)
	const pairStatus = getPairStatus(raw)

	return {
		...raw,
		app_version: raw.app_version || '',
		// app_version: '3.0.1', // Test low app version
		device_location_active: Boolean(raw.device_location_active),
		displayName: getDeviceDisplayName(raw),
		error_count: raw.error_count || 0,
		make,
		management_removed: Boolean(raw.management_removed),
		model,
		os,
		// os_version: '4.11.1', // Test low OS version
		pairStatus,
		// pairStatus: os == 'Android' ? 'not_paired' : pairStatus, // Test Android not paired
		// pairStatus: os == 'iOS' ? 'quick_pair' : pairStatus, // Test iOS quick pair
		// pairStatus: os == 'iOS' ? 'not_paired' : pairStatus, // Test iOS not paired
		suspended: Boolean(raw.suspended),
		// system_components: os == 'iOS' ? null : raw.system_components, // Test iOS no child app
		users: raw.users || [],
	}
}

const mapFeatureRequirementResult = (
	{ minAppVersion, minOSVersion, pairRequired, requireChildApp }: FeatureRequirement,
	d: ManagedDevice,
): FeatureRequirementResult => ({
	minAppVersion: hasMinFeatVer(minAppVersion, d),
	minOSVersion: !minOSVersion || hasMinOSVer(minOSVersion, d),
	pairRequired: PairStatuses[d.pairStatus] >= PairStatuses[pairRequired],
	requireChildApp: !requireChildApp || hasIOSChildApp(d),
	device_id: d.device_id,
})

/**
 * For Android, we normalize a bit:
 * - androidMinOSVersion defaults to minOSVersion
 * - androidMinAppVersion defaults to minAppVersion
 * - requireChildApp is always false (because it's installed and can't be uninstalled)
 */
const mapAndroidFeatureRequirementResult = (
	{
		minAppVersion,
		minOSVersion,
		androidMinOSVersion = minOSVersion,
		androidMinAppVersion = minAppVersion,
		pairRequired,
	}: FeatureRequirement,
	d: ManagedDevice,
) =>
	mapFeatureRequirementResult(
		{
			minAppVersion: androidMinAppVersion,
			minOSVersion: androidMinOSVersion,
			pairRequired,
			requireChildApp: false,
		},
		d,
	)

const isFeatureRequirementSatisfied = (featureReq: FeatureRequirement, d: ManagedDevice) =>
	Object.values(getFeatureRequirementResult(featureReq, d)).every(Boolean)

export const getFeatureRequirementResult = curry(
	(featureReq: FeatureRequirement, d: ManagedDevice) =>
		isAndroidDevice(d) ?
			mapAndroidFeatureRequirementResult(featureReq, d)
		:	mapFeatureRequirementResult(featureReq, d),
)

export const isFeatureCapableAndroidDevice = curry(
	(featureReq: FeatureRequirement, d: ManagedDevice) =>
		isAndroidDevice(d) && isFeatureRequirementSatisfied(featureReq, d),
)

export const isAndroidRestrixCapableDevice = curry(
	(minOSVer: number, d: ManagedDevice) =>
		isFeatureCapableAndroidDevice(FeatureRequirements.RESTRIX, d) && hasMinOSVer(minOSVer, d),
)

export const isFeatureCapableIOSDevice = curry(
	(req: FeatureRequirement, d: ManagedDevice) =>
		isIOSDevice(d) && isFeatureRequirementSatisfied(req, d),
)

export const isFeatureCapableDevice = curry(
	(req: FeatureRequirement, d: ManagedDevice) =>
		isFeatureCapableAndroidDevice(req, d) || isFeatureCapableIOSDevice(req, d),
)

export const hasFeatureCapableDevice = curry(
	(featureReq: FeatureRequirement, devices: ManagedDevice[]) =>
		devices.some(isFeatureCapableDevice(featureReq)),
)

export const hasFeatureCapableAndroidDevice = (
	featureReq: FeatureRequirement,
	devices: ManagedDevice[],
) => devices.some(isFeatureCapableAndroidDevice(featureReq))

export const hasFeatureCapableIOSDevice = (
	featureReq: FeatureRequirement,
	devices: ManagedDevice[],
) => devices.some(isFeatureCapableIOSDevice(featureReq))

export const hasAndroidRestrixCapableDevice = (minOSVer: number, devices: ManagedDevice[]) =>
	devices.some(isAndroidRestrixCapableDevice(minOSVer))

export const getFeatureRequirementResults = (
	featureReq: FeatureRequirement,
	devices: ManagedDevice[],
) => devices.map(getFeatureRequirementResult(featureReq))
